import React, { useState, useCallback, useEffect } from 'react';
import type { StoryModel, StoryStep, StepShape } from './storyModel';
import ThemePanel from './ThemePanel';
import DraftDialog, { type DraftAccess } from './DraftDialog';
import type { DraftMode, DraftRequest, DraftResponse } from '../cloud/PresentationApiClient';

/**
 * EditorOverlay — the authoring surface.
 *
 * Design decision (Alex, 2026-08-03): the presentation itself IS the editor. The 3D view
 * stays full-bleed and the tools float above it, instead of squeezing the product promise
 * into a third of the screen. That also makes the same layout work on a phone, where a
 * three-pane editor is unusable.
 *
 * Structure: a station strip along the bottom, a property panel on the side, both
 * dismissible. Nothing here touches Markdown — all mutations go through StoryModel
 * (owned by product/story).
 */

const RED = '#C1121C';
const RED_DEEP = '#8E0C14';
const CREAM = '#FBF7F4';
const sans = "system-ui, -apple-system, 'Segoe UI', sans-serif";
const serif = "'Iowan Old Style', Palatino, Georgia, serif";

const glass: React.CSSProperties = {
    background: 'rgba(20,10,12,0.82)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(251,247,244,0.16)',
    color: CREAM,
    fontFamily: sans,
};

const SHAPES: { id: StepShape; label: string }[] = [
    { id: 'sphere', label: 'Kugel' },
    { id: 'tile', label: 'Tafel' },
    { id: 'hexagon', label: 'Sechseck' },
    { id: 'box', label: 'Würfel' },
    { id: 'cylinder', label: 'Zylinder' },
    { id: 'gltf', label: '3D-Modell' },
];

const SWATCHES = ['#fff5ee', '#C1121C', '#F2B705', '#3AA655', '#2F6FED', '#111111'];

export interface EditorOverlayProps {
    model: StoryModel;
    /** Fired when the user picks a station — the host flies the camera there. */
    onFocusStep?: (stepId: string, index: number) => void;
    onOpenMarkdown?: () => void;
    saveState?: 'local' | 'dirty' | 'saving' | 'saved' | 'saveError';
    onSave?: () => void;
    onUploadMedia?: (file: File) => Promise<string>;
    /** Draft generation (contract p-b8b361e72d75); absent = feature not wired by the host. */
    onCreateDraft?: (request: DraftRequest) => Promise<DraftResponse>;
    draftAccess?: DraftAccess;
}

export const EditorOverlay: React.FC<EditorOverlayProps> = ({
    model, onFocusStep, onOpenMarkdown, saveState = 'local', onSave, onUploadMedia, onCreateDraft, draftAccess,
}) => {
    const [panelOpen, setPanelOpen] = useState(true);
    const [themeOpen, setThemeOpen] = useState(false);
    // `?draft=media|prompt` opens the dialog on arrival (landing page deep link).
    const [draftOpen, setDraftOpen] = useState<DraftMode | null>(() => {
        try {
            const fromUrl = new URLSearchParams(window.location.search).get('draft');
            // The editor mounts twice on the way from /edit/new to /edit/<id>; the intent
            // therefore stays in storage (60 s) and is cleared when the dialog closes.
            const stored = sessionStorage.getItem('3dp.draft-intent');
            const [mode, ts] = (stored || '').split(':');
            const fresh = mode && Date.now() - Number(ts || 0) < 60_000 ? mode : null;
            if (stored && !fresh) sessionStorage.removeItem('3dp.draft-intent');
            const v = fromUrl || fresh;
            return v === 'media' || v === 'prompt' ? v : null;
        } catch { return null; }
    });
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 820);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const selected = model.steps.find(s => s.id === model.selectedId) || null;

    const pick = useCallback((step: StoryStep, index: number) => {
        model.select(step.id);
        onFocusStep?.(step.id, index);
        setPanelOpen(true);
        // On a phone only one sheet fits above the station strip.
        if (narrow) setThemeOpen(false);
    }, [model, onFocusStep, narrow]);

    // Keyboard: undo/redo work everywhere except inside text fields.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const t = e.target as HTMLElement;
            if (t && /^(INPUT|TEXTAREA)$/.test(t.tagName)) return;
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                e.shiftKey ? model.redo() : model.undo();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [model]);

    useEffect(() => {
        const updateViewport = () => setNarrow(window.innerWidth < 820);
        window.addEventListener('resize', updateViewport);
        return () => window.removeEventListener('resize', updateViewport);
    }, []);

    const saveLabel: Record<string, string> = {
        local: 'Nur auf diesem Gerät', dirty: 'Nicht gespeichert', saving: 'Wird gespeichert …',
        saved: 'Gespeichert', saveError: 'Speichern fehlgeschlagen',
    };

    const field = (label: string, node: React.ReactNode) => (
        <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.6, marginBottom: 7, fontWeight: 700 }}>{label}</div>
            {node}
        </div>
    );

    const inputStyle: React.CSSProperties = {
        width: '100%', boxSizing: 'border-box', background: 'rgba(251,247,244,0.08)',
        border: '1px solid rgba(251,247,244,0.2)', borderRadius: 9, padding: '10px 12px',
        color: CREAM, fontSize: 14.5, fontFamily: sans,
    };

    return (
        <>
            {/* ---------- Kopfzeile: Zustand + Aktionen ---------- */}
            <div style={{ position: 'absolute', top: 14, left: 14, zIndex: 1250, display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ ...glass, borderRadius: 11, padding: '8px 13px', fontSize: 12.5, opacity: 0.95 }}>
                    {saveLabel[saveState]}
                </div>
                <button data-testid="undo-story" onClick={() => model.undo()} disabled={!model.canUndo} title="Rückgängig (Cmd+Z)"
                        style={{ ...glass, borderRadius: 11, padding: '8px 12px', fontSize: 14, cursor: model.canUndo ? 'pointer' : 'default', opacity: model.canUndo ? 1 : 0.4 }}>↺</button>
                <button data-testid="redo-story" onClick={() => model.redo()} disabled={!model.canRedo} title="Wiederholen"
                        style={{ ...glass, borderRadius: 11, padding: '8px 12px', fontSize: 14, cursor: model.canRedo ? 'pointer' : 'default', opacity: model.canRedo ? 1 : 0.4 }}>↻</button>
                {onSave && (
                    <button onClick={onSave} style={{ ...glass, background: RED, borderRadius: 11, padding: '8px 15px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        Speichern
                    </button>
                )}
                {onCreateDraft && draftAccess && (
                    <button data-testid="open-draft" onClick={() => setDraftOpen('media')} title="Entwurf aus Medien oder einem Satz erzeugen"
                            style={{ ...glass, borderRadius: 11, padding: '8px 13px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Erzeugen</button>
                )}
                <button data-testid="open-theme" onClick={() => setThemeOpen(v => !v)} title="Thema und Farben"
                        style={{ ...glass, background: themeOpen ? RED : glass.background, borderRadius: 11, padding: '8px 13px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Thema</button>
            </div>

            {themeOpen && <ThemePanel model={model} narrow={narrow} onClose={() => setThemeOpen(false)} />}
            {draftOpen && onCreateDraft && draftAccess && (
                <DraftDialog model={model} access={draftAccess} initialMode={draftOpen} onCreateDraft={onCreateDraft} onUploadMedia={onUploadMedia} onClose={() => { setDraftOpen(null); try { sessionStorage.removeItem('3dp.draft-intent'); } catch {} }} />
            )}

            {/* ---------- Eigenschaften der gewählten Station ---------- */}
            {selected && panelOpen && !(narrow && themeOpen) && (
                <div style={{
                    ...glass, position: 'absolute', zIndex: 1240, borderRadius: 16, padding: 18,
                    ...(narrow
                        ? { left: 12, right: 12, bottom: 128, maxHeight: '46vh', overflowY: 'auto' }
                        : { right: 16, top: 70, width: 306, maxHeight: 'calc(100vh - 210px)', overflowY: 'auto' }),
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <div style={{ fontFamily: serif, fontSize: 19 }}>Station</div>
                        <div onClick={() => setPanelOpen(false)} style={{ cursor: 'pointer', opacity: 0.6, fontSize: 18, lineHeight: 1 }}>×</div>
                    </div>

                    {field('Titel', (
                        <input data-testid="story-title" value={selected.title} style={inputStyle}
                               onChange={e => model.update(selected.id, { title: e.target.value })} />
                    ))}

                    {field('Text', (
                        <textarea data-testid="story-body" value={selected.body} rows={3} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.45 }}
                                  onChange={e => model.update(selected.id, { body: e.target.value })} />
                    ))}

                    {field('Form', (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {SHAPES.map(sh => (
                                <button key={sh.id} onClick={() => model.update(selected.id, { shape: sh.id })}
                                        style={{
                                            background: selected.shape === sh.id ? RED : 'rgba(251,247,244,0.08)',
                                            border: `1px solid ${selected.shape === sh.id ? RED : 'rgba(251,247,244,0.2)'}`,
                                            color: CREAM, borderRadius: 8, padding: '7px 11px', fontSize: 12.5, cursor: 'pointer', fontFamily: sans,
                                        }}>{sh.label}</button>
                            ))}
                        </div>
                    ))}

                    {field('Farbe', (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            {SWATCHES.map(c => (
                                <div key={c} onClick={() => model.update(selected.id, { color: c })}
                                     style={{
                                         width: 26, height: 26, borderRadius: '50%', background: c, cursor: 'pointer',
                                         border: selected.color === c ? `2px solid ${CREAM}` : '1px solid rgba(251,247,244,0.3)',
                                         boxShadow: selected.color === c ? '0 0 0 2px rgba(193,18,28,0.9)' : 'none',
                                     }} />
                            ))}
                        </div>
                    ))}

                    {field('Größe', (
                        <input type="range" min={0.3} max={2} step={0.05} value={selected.scale ?? 0.85}
                               onChange={e => model.update(selected.id, { scale: parseFloat(e.target.value) })}
                               style={{ width: '100%', accentColor: RED }} />
                    ))}

                    {field('Medium', (
                        <div style={{ display: 'grid', gap: 7 }}>
                            <input value={selected.uri ?? ''} placeholder="Bild, Video oder 3D-Modell …" style={inputStyle}
                                   onChange={e => model.update(selected.id, { uri: e.target.value || undefined })} />
                            {onUploadMedia && (
                                <div style={{ fontSize: 11.5, lineHeight: 1.4, opacity: 0.7 }}>
                                    Hochgeladene Medien sind über ihren Link öffentlich abrufbar. Der Entwurf selbst bleibt privat.
                                </div>
                            )}
                            {onUploadMedia && (
                                <label style={{ ...inputStyle, display: 'grid', placeItems: 'center', cursor: uploading ? 'wait' : 'pointer', fontSize: 12.5, opacity: uploading ? 0.55 : 1 }}>
                                    {uploading ? 'Wird hochgeladen …' : 'Datei aus der Cloud-Mediathek hinzufügen'}
                                    <input
                                        data-testid="upload-story-media"
                                        type="file"
                                        accept="image/*,audio/*,video/*,.glb,.gltf"
                                        disabled={uploading}
                                        style={{ display: 'none' }}
                                        onChange={async e => {
                                            const file = e.target.files?.[0];
                                            e.target.value = '';
                                            if (!file) return;
                                            setUploading(true);
                                            setUploadError(null);
                                            try {
                                                const uri = await onUploadMedia(file);
                                                model.update(selected.id, { uri });
                                            } catch (reason) {
                                                setUploadError(reason instanceof Error ? reason.message : 'Upload fehlgeschlagen.');
                                            } finally {
                                                setUploading(false);
                                            }
                                        }}
                                    />
                                </label>
                            )}
                            {uploadError && <div style={{ color: '#ff9ca3', fontSize: 12 }}>{uploadError}</div>}
                        </div>
                    ))}

                    {field('Reihenfolge', (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
                            <button
                                data-testid="move-step-left"
                                disabled={model.steps.findIndex(s => s.id === selected.id) <= 0}
                                onClick={() => {
                                    const index = model.steps.findIndex(s => s.id === selected.id);
                                    model.reorder(index, index - 1);
                                }}
                                style={{ ...inputStyle, cursor: 'pointer', opacity: model.steps.findIndex(s => s.id === selected.id) <= 0 ? 0.35 : 1 }}
                            >← Früher</button>
                            <button
                                data-testid="move-step-right"
                                disabled={model.steps.findIndex(s => s.id === selected.id) >= model.steps.length - 1}
                                onClick={() => {
                                    const index = model.steps.findIndex(s => s.id === selected.id);
                                    model.reorder(index, index + 1);
                                }}
                                style={{ ...inputStyle, cursor: 'pointer', opacity: model.steps.findIndex(s => s.id === selected.id) >= model.steps.length - 1 ? 0.35 : 1 }}
                            >Später →</button>
                        </div>
                    ))}

                    <button onClick={() => model.remove(selected.id)} disabled={model.steps.length <= 1}
                            style={{ background: 'transparent', border: '1px solid rgba(251,247,244,0.25)', color: CREAM,
                                     borderRadius: 9, padding: '9px 14px', fontSize: 13, width: '100%',
                                     cursor: model.steps.length > 1 ? 'pointer' : 'default', opacity: model.steps.length > 1 ? 0.85 : 0.35 }}>
                        Station löschen
                    </button>
                </div>
            )}

            {/* ---------- Stationsleiste ---------- */}
            {/* Above the side panels (1240): the strip must stay clickable on small heights (Codex, Post #4963). */}
            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 1245, padding: '0 12px 12px' }}>
                <div style={{ ...glass, borderRadius: 16, padding: 10, display: 'flex', gap: 8, alignItems: 'center', overflowX: 'auto' }}>
                    {model.steps.map((step, i) => (
                        <div key={step.id}
                             data-testid={`story-step-${i}`}
                             draggable
                             onDragStart={() => setDragIndex(i)}
                             onDragOver={e => e.preventDefault()}
                             onDrop={() => { if (dragIndex !== null) model.reorder(dragIndex, i); setDragIndex(null); }}
                             onClick={() => pick(step, i)}
                             title={step.title}
                             style={{
                                 flex: '0 0 auto', minWidth: 122, maxWidth: 168, cursor: 'pointer',
                                 background: step.id === model.selectedId ? RED : 'rgba(251,247,244,0.07)',
                                 border: `1px solid ${step.id === model.selectedId ? RED : 'rgba(251,247,244,0.16)'}`,
                                 borderRadius: 11, padding: '9px 11px',
                             }}>
                            <div style={{ fontSize: 10.5, letterSpacing: 1.6, opacity: 0.65, fontWeight: 700, marginBottom: 3 }}>
                                {String(i + 1).padStart(2, '0')}
                            </div>
                            <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {step.title || 'Ohne Titel'}
                            </div>
                        </div>
                    ))}
                    <div data-testid="add-story-step" onClick={() => model.add(model.selectedId)} title="Station hinzufügen"
                         style={{ flex: '0 0 auto', cursor: 'pointer', border: '1px dashed rgba(251,247,244,0.34)',
                                  borderRadius: 11, padding: '13px 15px', fontSize: 17, lineHeight: 1 }}>+</div>
                    {onOpenMarkdown && (
                        <div data-testid="open-markdown" onClick={onOpenMarkdown} title="Markdown bearbeiten"
                             style={{ flex: '0 0 auto', marginLeft: 'auto', cursor: 'pointer', opacity: 0.68, fontSize: 12, padding: '0 8px', whiteSpace: 'nowrap' }}>
                            Markdown
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default EditorOverlay;
